import React, { createContext, useContext, useState } from "react";
import { EventActionConfig } from "@closerplatform/spinner-openapi";
import {
  getLanguageOptionFromBrowser,
  LanguageOption
} from "@fidget/common/components/form/languagePicker/languagePicker";
import { usePreloader, withPreloading } from "../../HOC/withPreloading/withPreloading";
import { ExistingActionFormType, NewActionFormType } from "../eventActionsBot/types";
import {
  copyExistingEventActionForm,
  createNewEventActionForm,
  mapEventActionToForm,
  mapFormToCreateForm,
  mapFormToUpdateForm
} from "../eventActionsBot/utils";
import { ListHookEditable } from "../models/useSettingsList/types";
import { useSettingsListEditable } from "../models/useSettingsList/useSettingsList";
import { useAgentServices } from "../../../models/agentServices";

interface CurrentLanguage {
  currentLanguage: LanguageOption;
  setCurrentLanguage(value: LanguageOption): void;
}
type ProviderValue = ListHookEditable<ExistingActionFormType, NewActionFormType> & CurrentLanguage;

const useEventActionsBotSettingsState = (): ProviderValue => {
  const { spinnerService } = useAgentServices();
  const { setLoadingState } = usePreloader();
  const [locale, setLocale] = useState<LanguageOption>(getLanguageOptionFromBrowser());

  const listHook = useSettingsListEditable<
    ExistingActionFormType,
    NewActionFormType,
    EventActionConfig
  >({
    apiGetItems: () => spinnerService.getEventActionsConfigs(),
    apiDeleteItem: (id) => spinnerService.deleteEventActionConfig(id),
    apiUpdateItem: (form) =>
      spinnerService.updateEventActionConfig(form.$.id.$, mapFormToUpdateForm(form)),
    apiCreateItem: (form) =>
      spinnerService.createEventActionConfig(mapFormToCreateForm(form, locale)),
    mapItemToForm: mapEventActionToForm,
    createNewForm: createNewEventActionForm,
    copyExistingForm: copyExistingEventActionForm,
    onInitializeResult: setLoadingState,
    saveErrorId: "error.something"
  });

  return { ...listHook, currentLanguage: locale, setCurrentLanguage: setLocale };
};

const EventActionsSettingsContext = createContext<ProviderValue | undefined>(undefined);
EventActionsSettingsContext.displayName = "EventActionsSettingsContext"; // for name to display in dev tools

export const EventActionsSettingsProvider = withPreloading(
  ({ children }: React.PropsWithChildren<{}>) => {
    const value = useEventActionsBotSettingsState();

    return (
      <EventActionsSettingsContext.Provider value={value}>
        {children}
      </EventActionsSettingsContext.Provider>
    );
  }
);

export const useEventActionsSettings = (): ProviderValue => {
  const context = useContext(EventActionsSettingsContext);

  if (context === undefined) {
    throw new Error("useEventActionsSettings must be used within a EventActionsSettingsProvider");
  }

  return context as ProviderValue;
};
